import { format } from "date-fns";
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import type { UsersOrderData } from "@/types";
import { Badge } from "@/components/ui/badge";
import getStatusBadgeVariant from "@/utils/getStatusBadgeVariant";
import getStatusLabel from "@/utils/getStatusLabel";
import { Button } from "@/components/ui/button";

interface ActiveOrdersTabProps {
	ordersData: UsersOrderData[];
	setCurrentOrder: (order: UsersOrderData) => void;
	isLoading: boolean;
	searchQuery: string;
}

const ITEMS_PER_PAGE = 10;

const ActiveOrdersTab = ({
	ordersData,
	setCurrentOrder,
	searchQuery,
}: ActiveOrdersTabProps) => {
	const [currentPage, setCurrentPage] = useState(1);

	const activeOrders = ordersData.filter(
		(order) => order.confirmed && !order.delivered && !order.cancelled,
	);

	const filteredOrders = activeOrders.filter((order) => {
		if (!searchQuery) return true;
		const searchLower = searchQuery.toLowerCase();
		return (
			order.user.fullName?.toLowerCase().includes(searchLower) ||
			format(new Date(order.collectionDate), "PPP")
				.toLowerCase()
				.includes(searchLower) ||
			order.totalPrice.toString().includes(searchLower)
		);
	});

	const ordersByStatus = {
		confirmed: filteredOrders.filter((order) => !order.collected),
		collected: filteredOrders.filter(
			(order) => order.collected && !order.delivered,
		),
	};

	const totalPages = Math.ceil(
		Math.max(ordersByStatus.confirmed.length, ordersByStatus.collected.length) /
			ITEMS_PER_PAGE,
	);

	const getPaginatedOrders = (orders: UsersOrderData[]) => {
		const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
		return orders.slice(startIndex, startIndex + ITEMS_PER_PAGE);
	};

	const handleOrderClick = (order: UsersOrderData) => {
		setCurrentOrder(order);
	};

	const handleKeyDown = (event: React.KeyboardEvent, order: UsersOrderData) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			handleOrderClick(order);
		}
	};

	return (
		<div className="grid gap-4">
			<Card>
				<CardHeader>
					<CardTitle>Active Orders</CardTitle>
					<CardDescription>Orders that need attention</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="grid gap-6 md:grid-cols-2">
						<div className="space-y-4">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-medium">Ready for Collection</h3>
								<span className="text-sm text-muted-foreground">
									{ordersByStatus.confirmed.length} orders
								</span>
							</div>
							{getPaginatedOrders(ordersByStatus.confirmed).map((order) => (
								<button
									key={order.id}
									type="button"
									onClick={() => handleOrderClick(order)}
									onKeyDown={(e) => handleKeyDown(e, order)}
									className="flex w-full items-center justify-between rounded-lg border p-4 hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
								>
									<div>
										<p className="font-medium">{order.user.fullName}</p>
										<p className="text-sm text-muted-foreground">
											{format(new Date(order.collectionDate), "PPP")}
										</p>
									</div>
									<div className="text-right">
										<Badge
											className={`text-xs text-white ${getStatusBadgeVariant(
												order,
											)}`}
										>
											{getStatusLabel(order)}
										</Badge>
										<p className="mt-1 font-medium">
											£{order.totalPrice.toFixed(2)}
										</p>
									</div>
								</button>
							))}
						</div>

						<div className="space-y-4">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-medium">Ready for Delivery</h3>
								<span className="text-sm text-muted-foreground">
									{ordersByStatus.collected.length} orders
								</span>
							</div>
							{getPaginatedOrders(ordersByStatus.collected).map((order) => (
								<button
									key={order.id}
									type="button"
									onClick={() => handleOrderClick(order)}
									onKeyDown={(e) => handleKeyDown(e, order)}
									className="flex w-full items-center justify-between rounded-lg border p-4 hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
								>
									<div>
										<p className="font-medium">{order.user.fullName}</p>
										<p className="text-sm text-muted-foreground">
											{format(new Date(order.collectionDate), "PPP")}
										</p>
									</div>
									<div className="text-right">
										<Badge
											className={`text-xs text-white ${getStatusBadgeVariant(
												order,
											)}`}
										>
											{getStatusLabel(order)}
										</Badge>
										<p className="mt-1 font-medium">
											£{order.totalPrice.toFixed(2)}
										</p>
									</div>
								</button>
							))}
						</div>
					</div>

					{totalPages > 1 && (
						<div className="mt-6 flex items-center justify-center gap-2">
							<Button
								variant="outline"
								size="icon"
								onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
								disabled={currentPage === 1}
							>
								<ChevronLeft className="h-4 w-4" />
							</Button>
							<span className="text-sm text-muted-foreground">
								Page {currentPage} of {totalPages}
							</span>
							<Button
								variant="outline"
								size="icon"
								onClick={() =>
									setCurrentPage((prev) => Math.min(prev + 1, totalPages))
								}
								disabled={currentPage === totalPages}
							>
								<ChevronRight className="h-4 w-4" />
							</Button>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
};

export default ActiveOrdersTab;
